import { BoundSelectField, type BoundSelectFieldProps } from "@homebound/beam";
import { useCallback, useMemo, useState } from "react";
import type { MaterialVariantsFilter } from "src/generated/graphqlTypes";
import {
  type AddEditItemModal_MaterialVariant_InfoFragment,
  type AddEditItemModal_MaterialVariant_SelectedFragment,
  useAddEditItemModal_GetMaterialVariantLazyQuery,
  useAddEditItemModal_GetMaterialVariantsQuery,
} from "src/routes/libraries/plan-package/takeoffs/components/AddItems/AddEditItemModal.generated";

export type MaterialVariantSelectFieldProps = Omit<
  BoundSelectFieldProps<AddEditItemModal_MaterialVariant_InfoFragment, string>,
  "options" | "onSelect"
> & {
  //* * Default filter to be used when pulling variants */
  filter: MaterialVariantsFilter;
  initialOptions: AddEditItemModal_MaterialVariant_InfoFragment[];
  // onSelect receives the detailed Selected fragment with UOM and task mappings, instead of the search-only Info fragment.
  onSelect?: (id: string | undefined, option: AddEditItemModal_MaterialVariant_SelectedFragment | undefined) => void;
};

/** Searches variant labels and loads item defaults only after selection. */
export function MaterialVariantSelectField(allProps: MaterialVariantSelectFieldProps) {
  const { filter, initialOptions, onSelect = (id) => allProps.field.set(id), ...props } = allProps;
  const [search, setSearch] = useState<string>();
  const [selectedOption, setSelectedOption] = useState<AddEditItemModal_MaterialVariant_InfoFragment>();
  const [loadVariant, { loading, error }] = useAddEditItemModal_GetMaterialVariantLazyQuery({
    fetchPolicy: "cache-first",
    errorPolicy: "all",
  });
  const { data } = useAddEditItemModal_GetMaterialVariantsQuery({
    // A larger page keeps variants that share a code searchable without loading attribute editor data.
    // I.e. we have ~120 interior doors that all share the same code.
    variables: { filter: { ...filter, search }, page: { limit: 500 } },
    fetchPolicy: "cache-first",
    // Skip until the user types at least 2 characters to avoid expensive unfiltered queries
    skip: (search ?? "").length < 2,
  });

  const handleSelect = useCallback(
    async (id: string | undefined, option: AddEditItemModal_MaterialVariant_InfoFragment | undefined) => {
      if (!id || !option) {
        setSelectedOption(undefined);
        onSelect?.(undefined, undefined);
        return;
      }

      // Keep the previous form selection until its replacement's defaults have loaded successfully.
      const result = await loadVariant({ variables: { id } });
      if (!result.data || result.error) return;

      setSelectedOption(result.data.materialVariant);
      onSelect?.(id, result.data.materialVariant);
    },
    [loadVariant, onSelect],
  );

  const options = useMemo(
    () =>
      [
        ...(selectedOption ? [selectedOption] : []),
        ...initialOptions,
        ...(data?.materialVariants.entities ?? []),
      ].uniqueByKey("id"),
    [data, initialOptions, selectedOption],
  );

  return (
    <BoundSelectField
      placeholder="Type at least 2 characters to search"
      {...props}
      options={options}
      onSearch={setSearch}
      onSelect={handleSelect}
      disabled={loading || props.disabled}
      errorMsg={error?.message ?? props.errorMsg}
      helperText={
        data?.materialVariants.pageInfo.hasNextPage
          ? "Showing the first 500 matches. Refine your search to find more materials."
          : props.helperText
      }
    />
  );
}
